import React, { FC, useEffect, useState, useRef } from "react";
import axios from "axios";
import { DocumentEditor } from "@onlyoffice/document-editor-react";
import { Spin } from "antd";

interface OnlyOfficeEditorProps {
  htmlContent: string;
  onContentChange: (content: string) => void;
  reportId: string;
  documentTitle: string;
  documentServerUrl: string;
  editorInstanceRef: React.MutableRefObject<any>;
  readOnly?: boolean;
}

const BACKEND_ONLYOFFICE_URL = process.env.REACT_APP_API_BASE_URL;

// How long to keep polling after a save before giving up (ms)
const POLL_TIMEOUT_MS = 30000; // 30 seconds max
const POLL_INTERVAL_MS = 2000; // check every 2 seconds

const OnlyOfficeEditor: FC<OnlyOfficeEditorProps> = ({
  htmlContent,
  onContentChange,
  reportId,
  documentServerUrl,
  editorInstanceRef,
  readOnly = false,
  documentTitle,
}) => {
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const prevHtmlRef = useRef<string>(htmlContent);

  // Generate document key ONCE on mount — never changes during the session
  const documentKeyRef = useRef<string>(`${reportId}_${Date.now()}`);

  // Polling control
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isPollingActiveRef = useRef<boolean>(false);

  // STEP 1: Generate DOCX once on mount
  useEffect(() => {
    if (!reportId || fileUrl) return;

    const generateDocx = async () => {
      // console.log("[OnlyOffice] HTML CONTENT BEFORE:", htmlContent);
      try {
        const response = await axios.post(
          `${BACKEND_ONLYOFFICE_URL}/convert-html-to-docx`,
          { html: htmlContent, reportId },
          { headers: { "Content-Type": "application/json" } },
        );
        setFileUrl(response.data.fileUrl);
      } catch (error: any) {
        console.error("[OnlyOffice] DOCX generation failed:", error.message);
      }
    };

    generateDocx();
  }, [reportId]);

  // STEP 2: ForceSave every 30 seconds
  useEffect(() => {
    if (!reportId || readOnly) return;

    const forceSaveInterval = setInterval(() => {
      const editor = editorInstanceRef.current;
      if (!editor) return;

      try {
        editor.forceSave();
        // console.log("[OnlyOffice] forceSave triggered → starting poll");
        startPolling();
      } catch (err) {
        console.warn("[OnlyOffice] forceSave failed silently");
      }
    }, 30000); // every 30 seconds

    return () => clearInterval(forceSaveInterval);
  }, [reportId, readOnly]);

  // Cleanup on unmount
  useEffect(() => {
    return () => stopPolling();
  }, []);

  // Sync parent htmlContent prop → ref
  useEffect(() => {
    prevHtmlRef.current = htmlContent;
  }, [htmlContent]);

  // Polling helpers
  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    isPollingActiveRef.current = false;
    // console.log("[OnlyOffice] Polling stopped");
  };

  const startPolling = () => {
    // Guard: don't start a second poller if one is running
    if (isPollingActiveRef.current) {
      console.log("[OnlyOffice] Poll already active, skipping");
      return;
    }

    isPollingActiveRef.current = true;
    // console.log("[OnlyOffice] Polling started");

    // Safety timeout — stop polling after POLL_TIMEOUT_MS even if no HTML arrives
    pollingTimeoutRef.current = setTimeout(() => {
      console.warn("[OnlyOffice] Poll timed out — no HTML received");
      stopPolling();
    }, POLL_TIMEOUT_MS);

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const res = await axios.get(
          `${BACKEND_ONLYOFFICE_URL}/convert-docx-to-html/${reportId}`,
        );
        const newHtml = (res.data.html || "").trim();
        // console.log("[OnlyOffice] HTML CONTENT AFTER:", newHtml);

        if (newHtml && newHtml !== prevHtmlRef.current) {
          // console.log("[OnlyOffice] New HTML received → updating parent");
          prevHtmlRef.current = newHtml;
          onContentChange(newHtml);

          // Stop immediately — don't keep polling until next forceSave
          stopPolling();
        }
      } catch {
        // 404 means HTML not ready yet — keep polling silently
      }
    }, POLL_INTERVAL_MS);
  };

  if (!fileUrl) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <Spin size="large" tip="Preparing document..." />
      </div>
    );
  }

  const config = {
    document: {
      fileType: "docx",
      key: documentKeyRef.current,
      title: documentTitle,
      url: fileUrl,
    },
    documentType: "word",
    editorConfig: {
      mode: readOnly ? "view" : "edit",
      callbackUrl: `${BACKEND_ONLYOFFICE_URL}/onlyoffice-callback/${reportId}`,
      customization: {
        autosave: false,
        forcesave: true,
      },
    },
    events: {
      onReady: (e: any) => {
        editorInstanceRef.current = e.target;
        // console.log("[OnlyOffice] Editor ready");
        window.parent.postMessage({ type: "ONLYOFFICE_EDITOR_READY" }, "*");
      },
      onDocumentStateChange: (e: any) => {
        // e.data = true  → document has unsaved changes
        // e.data = false → document was just saved (Ctrl+S or forceSave completed)
        if (!e.data) {
          // console.log(
          //   "[OnlyOffice] Save detected via onDocumentStateChange → starting poll",
          // );
          startPolling();
          // ── NEW: notify OnlyOfficePage that user pressed Ctrl+S inside the iframe ──
          window.parent.postMessage({ type: "ONLYOFFICE_INTERNAL_SAVE" }, "*");
        }
      },
    },
  };

  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        overflow: "hidden",
        position: "absolute",
        top: 0,
        left: 0,
      }}
    >
      <DocumentEditor
        id="onlyoffice-editor"
        documentServerUrl={documentServerUrl}
        config={config}
        width="100%"
        height="100%"
      />
    </div>
  );
};

export default OnlyOfficeEditor;
